// Pure-data translate constants, deliberately kept in their OWN zod-free
// module (not in `apps/registry.ts`, which is heavy with zod schemas).
//
// The storefront's <LanguagePicker> renders in the root layout on every page
// and imports these. When they lived in `registry.ts`, that import dragged the
// entire ~108 KB zod runtime onto every browse page (home, tours, experience
// detail) — pages that never parse anything. Isolating the data here keeps the
// public bundle zod-free; `registry.ts` re-imports from here for its own use.

/**
 * Languages Google Translate's widget supports. Used to validate the active
 * `googtrans` cookie and to build the picker's option list, resolved
 * per-tenant from the agency's `defaultLocale`.
 */
export const GOOGLE_TRANSLATE_LANGUAGES: readonly string[] = [
  "af",
  "sq",
  "am",
  "ar",
  "hy",
  "az",
  "eu",
  "be",
  "bn",
  "bs",
  "bg",
  "ca",
  "ceb",
  "ny",
  "zh-CN",
  "zh-TW",
  "co",
  "hr",
  "cs",
  "da",
  "nl",
  "en",
  "eo",
  "et",
  "tl",
  "fi",
  "fr",
  "fy",
  "gl",
  "ka",
  "de",
  "el",
  "gu",
  "ht",
  "ha",
  "haw",
  "iw",
  "hi",
  "hmn",
  "hu",
  "is",
  "ig",
  "id",
  "ga",
  "it",
  "ja",
  "jw",
  "kn",
  "kk",
  "km",
  "ko",
  "ku",
  "ky",
  "lo",
  "la",
  "lv",
  "lt",
  "lb",
  "mk",
  "mg",
  "ms",
  "ml",
  "mt",
  "mi",
  "mr",
  "mn",
  "my",
  "ne",
  "no",
  "ps",
  "fa",
  "pl",
  "pt",
  "pa",
  "ro",
  "ru",
  "sm",
  "gd",
  "sr",
  "st",
  "sn",
  "sd",
  "si",
  "sk",
  "sl",
  "so",
  "es",
  "su",
  "sw",
  "sv",
  "tg",
  "ta",
  "te",
  "th",
  "tr",
  "uk",
  "ur",
  "uz",
  "vi",
  "cy",
  "xh",
  "yi",
  "yo",
  "zu",
];

/**
 * Storefront routes that must never be translated — the traveller's interactive
 * flows, kept fast and free of Google's DOM rewriting. The single source of
 * truth for both the bootstrap's idle auto-loader (in `registry.ts`) and the
 * picker's render gate, so the two can't drift and accidentally let Google's
 * element.js load on checkout.
 *
 * Every entry is a real storefront route, and every interactive route is here:
 * `apps/traveler-web/src/app/__tests__/translate-interactive-prefixes.spec.ts`
 * reads the route tree and fails if the two disagree. They had drifted —
 * `/book` and `/sign-up` matched nothing while the account pages and the claim
 * flow, which are the same kind of screen, stayed translatable. `/claim` is the
 * one that mattered most: it is where a guest sets a password, and Google
 * rewriting that DOM is the worst place for it.
 */
export const TRANSLATE_INTERACTIVE_PREFIXES: readonly string[] = [
  "/cart",
  "/checkout",
  "/confirm",
  "/pay",
  "/account",
  "/dashboard",
  "/overview",
  "/bookings",
  "/billing",
  "/profile",
  "/security",
  "/claim",
  "/sign-in",
];

/**
 * The Google Translate code for an agency's site language, or `null` when Google
 * cannot translate that language at all.
 *
 * These are two different vocabularies and nothing was translating between them.
 * The operator picks from `SUPPORTED_LANGUAGES`, which is WordPress core locale
 * codes (`ne_NP`, `es_ES`, `de_DE_formal`); Google's widget speaks its own
 * shorter set (`ne`, `es`, `de`), where only `zh-CN`/`zh-TW` carry a region and
 * three languages keep a legacy spelling. Handing Google a code it does not know
 * — as the picker did with `ne-NP` — leaves it unable to translate, and the
 * unmatched source also failed to drop its own language from the offered list,
 * so Nepali appeared twice and neither row worked.
 */
export function toGoogleTranslateCode(
  locale: string | null | undefined,
): string | null {
  const raw = (locale ?? "").trim();
  if (raw === "") return null;

  // `de_DE_formal` → `de-DE-formal`; the register suffix WordPress appends has
  // no place in a language tag, and the base match below drops it.
  const normalized = raw.replace(/_/g, "-").toLowerCase();
  const base = normalized.split("-")[0] ?? "";

  // Region-carrying codes first, so `zh-CN` keeps its region instead of
  // collapsing to a `zh` Google does not offer.
  const exact = GOOGLE_TRANSLATE_LANGUAGES.find(
    (code) => code.toLowerCase() === normalized,
  );
  if (exact) return exact;

  if (GOOGLE_TRANSLATE_LANGUAGES.includes(base)) return base;

  const alias = GOOGLE_TRANSLATE_ALIASES[base];
  if (alias) return alias;

  // `zh`, `zh-Hans` → the first code sharing the base. Only Chinese reaches this
  // today, and picking the first is what the browser-language path already did,
  // so both answer the same way.
  return (
    GOOGLE_TRANSLATE_LANGUAGES.find((code) => code.split("-")[0] === base) ??
    null
  );
}

/**
 * Site-language codes that reach Google under a different spelling.
 *
 * Two causes. Google keeps the pre-1989 `iw`/`jw` for Hebrew and Javanese,
 * offers one `no` for both written Norwegians, and files Filipino under `tl`.
 * Separately, WordPress names four languages by their three-letter ISO 639-2
 * code where Google uses the two-letter 639-1 one — each of the four IS offered
 * by Google, so without the bridge they would look untranslatable when they are
 * not. Languages Google genuinely does not offer (Aragonese, Dzongkha, Tibetan,
 * Sorbian, …) deliberately have no entry: `null` is the honest answer there.
 */
const GOOGLE_TRANSLATE_ALIASES: Record<string, string> = {
  he: "iw",
  jv: "jw",
  nb: "no",
  nn: "no",
  fil: "tl",
  bel: "be",
  kir: "ky",
  snd: "sd",
  yor: "yo",
};

/** What to declare as the source when the site language is one Google cannot
 *  translate from: Google detects the page's language itself. The same value the
 *  app's bootstrap already falls back to for `pageLanguage`. */
export const TRANSLATE_AUTO_SOURCE = "auto";
